import { Typography } from '@scaleaq/scaleui';
import { Combobox } from '@scaleaq/sui-react/combobox';
import styled, { css } from 'styled-components';

import { colors, responsive } from '@/utils';

type FieldColor = 'caution' | 'critical' | 'neutral';

const getFieldBorderColor = ({ $color }: { $color: FieldColor }) => {
  if ($color === 'critical') return colors.stroke.critical.solid['100'];
  if ($color === 'caution') return colors.stroke.caution.solid['300'];
  return colors.stroke.neutral['100'];
};

export const StyledLabel = styled(Combobox.Multiple.Label)`
  margin-bottom: ${responsive.spacing.xsmall};
`;

export const StyledDescription = styled(Typography)`
  margin: 0 0 ${responsive.spacing.xsmall};
`;

export const StyledField = styled(Combobox.Multiple.InputGroup)<{ $color: FieldColor }>`
  display: flex;
  align-items: center;
  gap: ${responsive.spacing.xsmall};
  box-sizing: border-box;
  width: 100%;
  min-height: 2.25rem;
  padding: ${responsive.spacing.xsmall};
  border: 1px solid ${getFieldBorderColor};
  border-radius: ${responsive.radius.xsmall};
  background: ${colors.background['100']};

  &:focus-within {
    outline: 2px solid ${colors.stroke.focused};
    outline-offset: -2px;
  }

  &[data-disabled] {
    background: ${colors.background.disabled};
    outline: none;
  }
`;

export const StyledChips = styled(Combobox.Multiple.Chips)`
  display: flex;
  flex: 1;
  min-width: 0;
`;

export const StyledValue = styled(Combobox.Multiple.Value)`
  display: flex;
  flex: 1;
  flex-wrap: wrap;
  align-items: center;
  gap: ${responsive.spacing.xsmall};
  min-width: 0;
  overflow: visible;
  white-space: normal;
  text-overflow: unset;
`;

export const StyledChip = styled(Combobox.Multiple.Chip)`
  display: inline-flex;
  align-items: center;
  gap: ${responsive.spacing.xsmall};
  max-width: 100%;
  border: 1px solid ${colors.stroke.neutral['100']};
  border-radius: ${responsive.radius.small};
  background: ${colors.background['100']};
  color: ${colors.text.neutral.on.soft['50']};
  padding: ${responsive.spacing.xsmall} ${responsive.spacing.small} ${responsive.spacing.xsmall}
    ${responsive.spacing.large};
  font-size: ${responsive.font.size.small};
`;

export const StyledChipClear = styled(Combobox.Multiple.ChipClear)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  background: transparent;
  color: inherit;
  cursor: pointer;
`;

export const StyledQueryInput = styled(Combobox.Multiple.Input)`
  && {
    flex: 1 1 6rem;
    width: auto;
    height: auto;
    min-width: 6rem;
    min-height: 1.5rem;
    border: none;
    background: transparent;
    color: ${colors.text.neutral.contrast};
  }

  && input {
    color: ${colors.text.neutral.contrast};
  }

  && input::placeholder {
    color: ${colors.text.neutral[100]};
  }

  &&:hover,
  &&:focus-within {
    border: none;
    background: transparent;
  }
`;

export const StyledPopup = styled(Combobox.Multiple.Popup)`
  color: ${colors.text.neutral.contrast};
`;

export const StyledGroupLabel = styled(Combobox.Multiple.GroupLabel)`
  display: block;
  padding: ${responsive.spacing.xsmall} ${responsive.spacing.small};
  color: ${colors.text.neutral[100]};
  font-size: ${responsive.font.size.small};
`;

const iconButton = css`
  &&,
  &&:hover,
  &&:focus-visible {
    display: inline-flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    width: 1.5rem;
    height: 1.5rem;
    padding: 0;
    border: none;
    background: transparent;
    color: ${colors.text.neutral.contrast};
  }

  && svg {
    width: 1rem;
    height: 1rem;
  }
`;

export const StyledClear = styled(Combobox.Multiple.Clear)`
  ${iconButton}

  &&,
  &&:hover,
  &&:focus-visible {
    cursor: pointer;
  }
`;

export const StyledChevron = styled(Combobox.Multiple.Trigger)`
  ${iconButton}

  &&,
  &&:hover,
  &&:focus-visible {
    min-width: 0;
  }
`;
